import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { api, matchingApi, resolveUrl } from "../../../../config/apis";
import AdminApi from "../../../../config/apis/admin";
import { DataLoader } from "../../../../helper/helperFunction";
import { SERVER_KEY } from "../../../../config/constants/EnviormentVariables";
import AiJobMatchingModal from "../../../../components/ai-job-matching/ai-job-matching-modal";
import styles from "./ai-job-matching-tab.module.css";

const buildResumeUrl = (resumePath) => {
  if (!resumePath) return null;
  if (resumePath.startsWith("http://") || resumePath.startsWith("https://"))
    return resumePath;
  const base = (SERVER_KEY || "").replace(/\/$/, "");
  const path = String(resumePath).replace(/^\//, "");
  return `${base}/${path}`;
};

export const AiJobMatchingTab = () => {
  const params = useParams();
  const jobId = useMemo(() => params?.id, [params?.id]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [jobTitle, setJobTitle] = useState("");

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    openModal();
    // Tab content mounts only when active, so this runs on tab click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchJobTitle = async () => {
      if (!jobId) return;
      try {
        const res = await api.get(resolveUrl(AdminApi.JOBS_DETAIL_VIEW, jobId));
        setJobTitle(res?.data?.data?.job_title || "");
      } catch (e) {
        setJobTitle("");
      }
    };

    fetchJobTitle();
  }, [jobId]);

  const handleSubmit = async (values) => {
    try {
      closeModal();
      setLoading(true);
      setError("");
      setData(null);

      const payload = {
        job_id: values.jobId,
        top_k: values.topK,
        filter_by_location: values.filterByLocation,
        filter_by_job_type: values.filterByJobType,
      };

      setLastRequest(payload);
      const res = await matchingApi.post(AdminApi.AI_JOB_MATCH, payload);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to fetch AI matches.");
    } finally {
      setLoading(false);
    }
  };

  const candidates = useMemo(() => {
    const list = Array.isArray(data?.top_candidates) ? data.top_candidates : [];
    return list.map((c, index) => ({ ...c, _rank: index + 1 }));
  }, [data]);

  const tableColumns = useMemo(
    () => [
      {
        selector: (row) => row._rank,
        name: "#",
        sortable: false,
        width: "70px",
      },
      {
        name: "Student",
        sortable: false,
        minWidth: "220px",
        cell: (row) => (
          <Box>
            <Typography sx={{ fontWeight: 700 }}>
              {`${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() ||
                `#${row.student_id}`}
            </Typography>
          </Box>
        ),
      },
      {
        selector: (row) => row.email || "-",
        name: "Email",
        sortable: false,
        minWidth: "220px",
        cell: (row) =>
          row.email ? (
            <Tooltip title={row.email} arrow>
              <Typography className={styles.muted}>{row.email}</Typography>
            </Tooltip>
          ) : (
            <Typography className={styles.muted}>-</Typography>
          ),
      },
      {
        selector: (row) => row.final_score ?? "-",
        name: "Final Score",
        sortable: false,
        width: "100px",
      },
      {
        name: "Location",
        sortable: false,
        minWidth: "160px",
        cell: (row) => (
          <Typography className={styles.muted}>
            {row.student_city || "-"}, {row.student_country || "-"}
          </Typography>
        ),
      },
      {
        name: "Matched Skills",
        sortable: false,
        minWidth: "220px",
        cell: (row) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(row.matched_skills || []).length ? (
              (row.matched_skills || []).map((s) => (
                <Typography
                  key={`${row.student_id}-matched-${s}`}
                  className={styles.matchedSkill}
                >
                  {s}
                </Typography>
              ))
            ) : (
              <Typography className={styles.muted}>-</Typography>
            )}
          </Box>
        ),
      },
      {
        name: "Missing Skills",
        sortable: false,
        minWidth: "220px",
        cell: (row) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(row.missing_skills || []).length ? (
              (row.missing_skills || []).map((s) => (
                <Typography
                  key={`${row.student_id}-missing-${s}`}
                  className={styles.missingSkill}
                >
                  {s}
                </Typography>
              ))
            ) : (
              <Typography className={styles.muted}>-</Typography>
            )}
          </Box>
        ),
      },
      {
        selector: (row) => row.student_job_types || "-",
        name: "Job Types",
        sortable: false,
        minWidth: "220px",
        cell: (row) => (
          <Typography className={styles.muted}>
            {row.student_job_types || "-"}
          </Typography>
        ),
      },
      {
        name: "Resume",
        sortable: false,
        width: "90px",
        cell: (row) => {
          const resumeUrl = buildResumeUrl(row.resume_path);
          return resumeUrl ? (
            <a
              className={styles.resumeLink}
              href={resumeUrl}
              target="_blank"
              rel="noreferrer"
            >
              View
            </a>
          ) : (
            <Typography className={styles.muted}>-</Typography>
          );
        },
      },
    ],
    []
  );

  return (
    <Box>
      {loading ? (
        <DataLoader />
      ) : error ? (
        <Box className={styles.table_wrap} sx={{ mt: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Unable to load results
          </Typography>
          <Typography className={styles.muted} sx={{ mb: 2 }}>
            {error}
          </Typography>
        </Box>
      ) : !data ? (
        <Box className={styles.table_wrap} sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            No results yet
          </Typography>
          <Typography className={styles.muted}>
            Complete the matching popup to generate results.
          </Typography>
        </Box>
      ) : (
        <Box className="container" style={{ marginTop: "50px" }}>
          <Box
            className={` table-phases table-skills_popup ${styles.table_wrap}`}
            marginTop={5}
            marginBottom={5}
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontWeight: 700 }}>Job</Typography>
                <Typography className={styles.muted}>
                  {jobTitle || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontWeight: 700 }}>Job Location</Typography>
                <Typography className={styles.muted}>
                  {data?.job_location || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontWeight: 700 }}>Job Type</Typography>
                <Typography className={styles.muted}>{data?.job_type || "-"}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  Required Job Skills
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {(data?.job_skills || []).map((skill) => (
                    <Chip key={skill} label={skill} variant="outlined" />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box />
              </Grid>
            </Grid>

            <DataTable
              columns={tableColumns}
              data={candidates}
              highlightOnHover
              pagination={false}
              noDataComponent={
                <Typography className={styles.muted}>
                  There are no candidates to display.
                </Typography>
              }
            />
          </Box>
        </Box>
      )}

      <AiJobMatchingModal
        open={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        defaultJobId={jobId}
        jobName={jobTitle}
        lockJobId={true}
      />
    </Box>
  );
};
